
import { useQuery, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionPlan, mapDbPlanToSubscriptionPlan } from './types';
import { Json } from '@/integrations/supabase/types';
import { queryClient } from './queryClient';

// Fetch all subscription plans
export const fetchSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .order('monthly_price');

  if (error) {
    console.error('Error fetching subscription plans:', error);
    throw error;
  }

  return data.map(plan => mapDbPlanToSubscriptionPlan(plan));
};

// Fetch only active subscription plans
export const fetchActiveSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('monthly_price');

  if (error) {
    console.error('Error fetching active subscription plans:', error);
    throw error;
  }

  return data.map(plan => mapDbPlanToSubscriptionPlan(plan));
};

// Fetch a specific subscription plan by ID
export const fetchSubscriptionPlanById = async (planId: string): Promise<SubscriptionPlan | null> => {
  if (!planId) return null;
  
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('id', planId)
    .single();
    
  if (error) {
    console.error('Error fetching subscription plan by ID:', error);
    throw error;
  }
  
  if (!data) return null;
  
  return mapDbPlanToSubscriptionPlan(data);
};

// Create a new subscription plan
export const createSubscriptionPlan = async (planData: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> => {
  const features = {
    currency_conversion: planData.features?.currency_conversion || false,
    multiple_currencies: planData.features?.multiple_currencies || false,
    price_comparison: planData.features?.price_comparison || false,
    grouped_contracts: planData.features?.grouped_contracts || false,
    contract_services: planData.features?.contract_services || false,
    reminders: planData.features?.reminders || false,
    payment_type_selection: planData.features?.payment_type_selection || false,
    dashboard: planData.features?.dashboard || false,
    contract_pdf_email: planData.features?.contract_pdf_email || false
  };

  const { data, error } = await supabase
    .from('subscription_plans')
    .insert({
      name: planData.name,
      description: planData.description || null,
      plan_type: planData.plan_type,
      monthly_price: planData.monthly_price,
      annual_price: planData.annual_price || null,
      features: features as unknown as Json,
      is_active: planData.is_active ?? true,
      show_on_landing: planData.show_on_landing ?? false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription plan:', error);
    throw error;
  }

  return mapDbPlanToSubscriptionPlan(data);
};

// Update an existing subscription plan
export const updateSubscriptionPlan = async (plan: SubscriptionPlan): Promise<SubscriptionPlan> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .update({
      name: plan.name,
      description: plan.description || null,
      plan_type: plan.plan_type,
      monthly_price: plan.monthly_price,
      annual_price: plan.annual_price || null,
      features: plan.features as unknown as Json,
      is_active: plan.is_active,
      show_on_landing: plan.show_on_landing,
      updated_at: new Date().toISOString()
    })
    .eq('id', plan.id)
    .select()
    .single();

  if (error) {
    console.error('Error updating subscription plan:', error);
    throw error;
  }

  return mapDbPlanToSubscriptionPlan(data);
};

// Delete a subscription plan
export const deleteSubscriptionPlan = async (planId: string): Promise<void> => {
  const { error } = await supabase
    .from('subscription_plans')
    .delete()
    .eq('id', planId);

  if (error) {
    console.error('Error deleting subscription plan:', error);
    throw error;
  }
};

// React Query hooks for plans
export const usePlanHooks = () => {
  // Query hooks
  const useSubscriptionPlansQuery = () => 
    useQuery({
      queryKey: ['subscription-plans'],
      queryFn: fetchSubscriptionPlans
    });

  const useActiveSubscriptionPlansQuery = () => 
    useQuery({
      queryKey: ['active-subscription-plans'],
      queryFn: fetchActiveSubscriptionPlans
    });

  const useSubscriptionPlanByIdQuery = (planId: string | undefined) => 
    useQuery({
      queryKey: ['subscription-plan', planId],
      queryFn: () => fetchSubscriptionPlanById(planId!),
      enabled: !!planId
    });

  // Mutation hooks
  const useCreateSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: createSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
      }
    });

  const useUpdateSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: updateSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
      }
    });

  const useDeleteSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: deleteSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
      }
    });

  return {
    useSubscriptionPlansQuery,
    useActiveSubscriptionPlansQuery,
    useSubscriptionPlanByIdQuery,
    useCreateSubscriptionPlanMutation,
    useUpdateSubscriptionPlanMutation,
    useDeleteSubscriptionPlanMutation
  };
};
